import React, { useEffect, useState } from "react";
import { Save, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Header } from "@/pages/admin/AdminDashboard";
import { Card, Field } from "@/pages/admin/AdminProfile";
import ImageUploader from "@/components/admin/ImageUploader";

const empty = {
    logo_url: "",
    favicon_url: "",
    whatsapp_number: "",
    whatsapp_default_message: "",
    whatsapp_floating_enabled: true,
    calendar_url: "",
    rotating_words: [],
    cta_headline_prefix: "Build something",
    cta_subheadline: "",
    show_services: true,
    show_testimonials: true,
    show_faq: true,
    show_clients: true,
    show_partnerships: true,
    show_certifications: true,
    slots_label: "",
    response_time_label: "",
    delivery_time_label: "",
};

export default function AdminSettings() {
    const [data, setData] = useState(empty);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/settings").then((r) => setData({ ...empty, ...r.data })).finally(() => setLoading(false));
    }, []);

    const update = (k) => (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setData((s) => ({ ...s, [k]: v }));
    };
    const updateCSV = (k) => (e) => {
        setData((s) => ({ ...s, [k]: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) }));
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/settings", data);
            toast.success("Settings saved");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="mono-label">Loading…</div>;

    return (
        <div data-testid="admin-settings-page">
            <Header
                title="Site Settings"
                subtitle="Konfigurasi global: WhatsApp button, CTA text, rotating words, dan tombol on/off untuk tiap section."
                action={
                    <button onClick={save} disabled={saving} data-testid="settings-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand text-white hover:bg-brand-hover disabled:opacity-60">
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
                    </button>
                }
            />

            <div className="grid lg:grid-cols-2 gap-6">
                <Card title="Branding & Images">
                    <Field label="Website Logo (di navbar)">
                        <ImageUploader value={data.logo_url} onChange={update("logo_url")} placeholder="Upload atau paste link logo..." />
                    </Field>
                    <Field label="Favicon (ikon di tab browser)">
                        <ImageUploader value={data.favicon_url} onChange={update("favicon_url")} placeholder="Upload icon (.png/.ico)..." />
                    </Field>
                </Card>

                <Card title="Floating WhatsApp Button">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" checked={data.whatsapp_floating_enabled} onChange={update("whatsapp_floating_enabled")} className="w-4 h-4 accent-brand" />
                        <span className="text-sm text-ink-100">Tampilkan floating WhatsApp button di public site</span>
                    </label>
                    <Field label="WhatsApp number (format internasional tanpa +, contoh: 62817277729)">
                        <input className="ainp" value={data.whatsapp_number} onChange={update("whatsapp_number")} placeholder="62817277729" data-testid="settings-whatsapp-number" />
                    </Field>
                    <Field label="Default pre-filled message">
                        <textarea rows={3} className="ainp" value={data.whatsapp_default_message} onChange={update("whatsapp_default_message")} />
                    </Field>
                </Card>

                <Card title="CTA Section">
                    <Field label="Headline prefix (e.g. 'Build something')">
                        <input className="ainp" value={data.cta_headline_prefix} onChange={update("cta_headline_prefix")} />
                    </Field>
                    <Field label="Rotating words (comma-separated, akan loop)">
                        <input className="ainp" value={(data.rotating_words || []).join(", ")} onChange={updateCSV("rotating_words")} placeholder="amazing, premium, bold, fast" data-testid="settings-rotating-words" />
                    </Field>
                    <Field label="Sub-headline (di bawah headline besar)">
                        <textarea rows={3} className="ainp" value={data.cta_subheadline} onChange={update("cta_subheadline")} />
                    </Field>
                    <Field label="Calendar URL (Cal.com / Calendly link). Kosongkan = scroll ke #contact">
                        <input className="ainp" value={data.calendar_url} onChange={update("calendar_url")} placeholder="https://cal.com/akhmadferdian/intro" />
                    </Field>
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="Response time"><input className="ainp" value={data.response_time_label} onChange={update("response_time_label")} placeholder="24 hours" /></Field>
                        <Field label="Delivery time"><input className="ainp" value={data.delivery_time_label} onChange={update("delivery_time_label")} placeholder="2 – 4 weeks" /></Field>
                        <Field label="Slots label"><input className="ainp" value={data.slots_label} onChange={update("slots_label")} placeholder="2 of 3 open" /></Field>
                    </div>
                </Card>

                <Card title="Section visibility" className="lg:col-span-2">
                    <p className="text-xs text-ink-300 mb-3">Toggle section yang ingin ditampilkan/disembunyikan tanpa kehilangan datanya.</p>
                    <div className="grid sm:grid-cols-3 gap-3">
                        {[
                            ["show_clients", "Clients (logo wall)"],
                            ["show_services", "Services"],
                            ["show_partnerships", "Partnerships"],
                            ["show_testimonials", "Testimonials"],
                            ["show_certifications", "Certifications"],
                            ["show_faq", "FAQ"],
                        ].map(([k, label]) => (
                            <label key={k} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-white/5 bg-ink-900/40">
                                <input type="checkbox" checked={data[k]} onChange={update(k)} className="w-4 h-4 accent-brand" data-testid={`toggle-${k}`} />
                                <span className="text-sm text-ink-100">{label}</span>
                            </label>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
}
